<template>
  <div v-if="packSizes" class="PackSize__Container">
    <span class="p4 PackSize__Title">{{ store?.$state?.terms?.bmsm?.title }}</span>
    <Tooltip :text="bmsmTooltipText" />
    <div class="PackSize__Buttons">
      <div v-for="tier in packSizes" :key="tier.threshold" class="PackSize__ButtonsWrap">
        <button
          class="button button--tertiary-a"
          :class="{
            PackSize__Button: true,
            'PackSize__Button--Active': quantity == tier.threshold,
          }"
          @click="updatePackSize(tier.threshold)"
        >
          {{ getPackSizeText(tier.threshold) }}
        </button>
        <span v-if="tier.discount" class="t5 PackSize__Savings" data-color="green">{{ store?.$state?.terms?.bmsm?.discount_text }} {{ tier.discount }}%</span>
      </div>
    </div>
  </div>
  <BackInStock :location="location" :qvData="qvBISData" v-if="(!store.$state.isBundle || isQV) && !available" />
  <template v-else>
    <div
      class="ProductForm__Actions"
      :class="{
        'ProductForm__Actions--ReviewBundle': isReviewStep && !isQV,
        'ProductForm__Actions--Building': isBuilding && !isQV,
      }"
    >
      <div class="ProductForm__ActionWrap">
        <template v-if="edit && !isQV">
          <div class="ProductForm__Cta--Container">
            <button
              role="button"
              class="ProductForm__Cta"
              :class="{
                'ProductForm__Cta--Inactive': !available,
              }"
              @click="handleEdit"
            >
              {{ cta }}
            </button>
            <Backorder v-if="shipsBy" :date="shipsBy" />
          </div>
        </template>
        <template v-else-if="isBuildStep && !isQV">
          <div class="ProductForm__Cta--Container">
            <button role="button" class="ProductForm__Cta ProductForm__Cta--Start" @click="store.step('+')">
              {{ cta }}
            </button>
          </div>
        </template>
        <template v-else-if="isBuilding && !isQV">
          <div class="ProductForm__Cta--Container">
            <button role="button" class="ProductForm__Cta ProductForm__Cta--Prev ProductForm__Cta--Inverted" @click="store.step('-')">
              {{ store.terms.cta.prev }}
            </button>
            <Backorder v-if="shipsBy" :date="shipsBy" />
          </div>

          <button role="button" class="ProductForm__Cta ProductForm__Cta--Next" :disabled="readyToProceed !== ''" :class="readyToProceed" @click="store.step('+')">
            {{ nextCta }}
          </button>
        </template>
        <template v-else>
          <div class="ProductForm__Cta--Container">
            <button
              role="button"
              class="ProductForm__Cta"
              data-ht-element="cta"
              :class="{
                'ProductForm__Cta--Inactive': !available,
                'Rise-add-to-cart-button': isGiftCard,
              }"
              @click="handleSubmit"
            >
              {{ cta }}
            </button>
            <Backorder v-if="shipsBy" :date="shipsBy" />
          </div>
          <div v-if="!packSizes && !isGiftCard" class="select-input--wrapper select-input--wrapper-pdp">
            <select name="quantity" aria-label="quantity selector" v-model="quantity" class="select-input p_2 p_2--bold">
              <option v-for="index in maxAllowedQuantity" :key="index" :value="index">
                {{ index }}
              </option>
            </select>
            <Icon :icon="'chevron-down'" />
          </div>
        </template>
      </div>
    </div>
  </template>
</template>

<script setup>
import { computed, ref, watchEffect, onMounted } from 'vue';
import Backorder from './Backorder.vue';
import { useProductStore } from './Composables/Store';
import { fetchStorefront, clean } from '@utils/GraphQLClient';
import { COMPONENT_FROM_SELECTIONS_QUERY } from './Composables/Queries';
import Tooltip from '../Global/Tooltip.vue';
import Icon from '../Global/Icon.vue';
import { pubSub } from '@utils/PubSub';
import { getCartInstances } from '@src/js/utils/global-functions';
import BackInStock from './BackInStock.vue';

const emits = defineEmits(['close']);
const props = defineProps({
  data: { type: Object, required: false },
  edit: { type: Boolean, required: false, default: false },
  isAutoBundle: { type: Boolean, required: false, default: false },
  isReviewStep: { type: Boolean, required: false, default: false },
  isModal: { type: Boolean, required: false, default: false },
  isGiftCard: { type: Boolean, required: false, default: false },
  location: { type: String },
});

const cta = ref('');
const quantity = ref(1);
const ctaEl = ref(false);
const bundlesInCart = ref(false);
const store = useProductStore();

const isBuildStep = computed(() => store.isBuildStep);
const isBuilding = computed(() => store.isBuilding);
const isQV = computed(() => props.location == 'quick-view');

const qvBISData = computed(() => {
  const bisData = {
    variantId: props.data.selected.variant.id,
    productId: props.data.selected.variant.product.id,
    url: props.data.item.onlineStoreUrl,
    img: props.data.gallery[0].image.url,
    price: parseFloat(props.data.item.priceRange.minVariantPrice.amount),
  };
  return bisData;
});

//BMSM Pack Sizes + Pack Size Quantity Logic

const updatePackSize = (packSize) => {
  const queryString = window.location.search;
  const params = new URLSearchParams(queryString);
  const defaultPackSize = findTiers().filter((tier) => tier.threshold == params.get('pack-size')).length > 0 ? params.get('pack-size') : findTiers()[0].threshold;
  params.set('pack-size', packSize || defaultPackSize);
  quantity.value = params.get('pack-size');
  if (!isQV.value) {
    const url = `${window.location.pathname}?${params}`;
    window.history.replaceState({}, '', url);
  }
};

const packSizes = computed(() => {
  if (findTiers().length > 0) updatePackSize();
  return findTiers().length > 0 ? findTiers() : false;
});

const getPackSizeText = (threshold) => {
  return threshold > 1 ? store?.$state?.terms?.bmsm?.pack_size_text + threshold : threshold;
};

const findTiers = () => {
  const tags = props?.data?.item?.tags;
  let bmsmTiers = [];
  if (tags?.find((tag) => tag.includes('Pack Size'))) {
    const bmsmTierData = tags
      .find((tag) => tag.includes('BMSM Tiers:'))
      ?.split(':')[1]
      .trim()
      .split('/');
    bmsmTierData?.forEach((tier) => {
      const tierData = tier.split('-');
      bmsmTiers.push({
        threshold: parseInt(tierData[0]),
        discount: parseInt(tierData[1]),
      });
    });
  }
  return bmsmTiers;
};

const bmsmTooltipText = computed(() => {
  const text = store?.$state?.terms?.tooltips?.bmsm;
  const tiers = findTiers();
  let newText = '';
  tiers.forEach((tier, i) => {
    const index = parseInt(i) + 1;
    if (tier.discount != 0) {
      const discountVariable = '{{discount}}';
      const thresholdVariable = tiers[index]?.threshold - tier.threshold > 1 || index == tiers.length ? '{{threshold}}' : '{{threshold}} or more';
      newText += text.replace(discountVariable, tier.discount).replace(thresholdVariable, tier.threshold);
      if (index != tiers.length) newText += '<br/>';
    }
  });
  return newText;
});

const shipsBy = computed(() => {
  const date = store.isSteppedBundle && !isQV.value ? store?.getCurrentStep?.selected?.variant?.shipsBy?.value : props?.data?.selected?.variant?.shipsBy?.value;
  return date || false;
});

// Cart Functions live in src/modules/Cart.ts
const createEvent = (items) =>
  new CustomEvent('theme:add-to-cart', {
    detail: items,
  });

const fetchCartInstances = () => {
  fetch('/cart.js')
    .then((response) => {
      return response.json();
    })
    .then((data) => {
      const productTitle = store.isSteppedBundle && !isQV.value ? store.$state.item.title : props?.data?.item?.title;
      bundlesInCart.value = parseInt(getCartInstances(productTitle, data));
    });
};

const getMaxBundles = () => {
  const tags = store.isSteppedBundle && !isQV.value ? store.$state.item.tags : props?.data?.item?.tags;
  return tags?.find((tag) => tag.includes('Max Bundles'))
    ? tags
        ?.find((tag) => tag.includes('Max Bundles'))
        .split(':')[1]
        .trim()
    : false;
};

pubSub.subscribe('cart:request', () => fetchCartInstances());

const findTprDiscount = (item, variant = false) => {
  const pogs = item?.promotedOptionGroups;
  if (!pogs) return 'null';
  const options = variant ? variant.selectedOptions : item?.variantBySelectedOptions?.selectedOptions;
  const values = options.map((option) => option.value);

  const index = pogs.findIndex((pog) => pog.values.find((value) => values.includes(value)));

  if (index <= -1) return 'null';

  const tpr = pogs[index]?.tpr?.value ?? -1;
  const tprDiscount = parseInt(tpr);

  return tprDiscount > 0 ? tprDiscount : 'null';
};

const handleSubmit = () => {
  let payload;
  const selections = isQV.value ? props.data.selected : store.$state.selected;
  const isBundle = (store.$state.isBundle && !isQV.value) ?? false;
  const hasComponents = isBundle && selections.components.length > 0;
  const item = isQV.value ? props.data.item : store.$state.item;

  const promo = window.Brooklinen?.promo?.discounts ?? {};
  const categoryId = promo?.category?.promoName ?? 'null';

  if (hasComponents) {
    /*
    This is preliminary work to check if adding a bundle product to the cart will exceed the max bundles set in the Max Bundles tag.
    We are not using this logic yet, but the future strategy in the code is as follows:
      - On page load for bundles, determine the max bundles set via tags, then determine the number of unique instances of the bundle in the cart.
      - On add to cart, we fire an event to Cart.ts. Upon a successful post to add or update endpoints, we emit a fetch event called cart:request. By listening to this event, we know that the cart has been updated, and so we can update the number of cart instances in our props.
      - Using the maxBundlesExceeded flag, we can either prevent the add to cart altogether, remove bundle line item properties from the payload, or toggle messaging that indicates to the user that they've hit the max bundles.
    */
    const maxBundlesExceeded = getMaxBundles() <= bundlesInCart.value;
    if (maxBundlesExceeded) console.log('Max Bundles Exceeded');
    const title = store.$state.item.title;
    const bundleUrl = store.$state.item.onlineStoreUrl ?? '';
    const components = Array.from(selections.components);
    const timestamp = Date.now();
    let current = 0;
    payload = components
      .reduce((bundle, component) => {
        const variant = component.variantBySelectedOptions || component.variants[0];
        const variantId = variant.id.split('ProductVariant/')[1].toString().trim();
        const productId = variant.product.id.split('Product/')[1].toString().trim();
        const comparePrice = variant?.compareAtPrice?.amount;
        const price = variant.price.amount;
        const tags = component.tags;
        const shellTags = item.tags;

        // Bundle discount tag is only applied to the parent product so you need to grab parent's tags
        const bundleDiscount = shellTags
          .find((tag) => tag.includes('Discount Amount:'))
          ?.split(':')[1]
          .trim();

        let categoryDiscount = 'null';
        // Since some components of a bundle do not qualifiy for category sales you need to check for this tag at component level
        const isCategory = () => tags.some((tag) => tag.includes('Category Promo:'));
        if (isCategory()) {
          categoryDiscount = tags
            .find((tag) => tag.includes('Category Promo:'))
            ?.split(':')[1]
            .trim();
        }

        // Temporary Price Reductions are component variants that get a reduction but are not part of any active sale ex: category sale
        const tprDiscount = findTprDiscount(component);

        const originalPrice = comparePrice > 1 ? comparePrice : price;
        const categoryPromoName = isCategory() ? categoryId : 'null';
        let savings = store.$state.bundleSavings[current];

        // For bundles tagged with Product Bundle No Discount
        if (isNaN(savings)) {
          savings = 0;
        }

        savings = savings.toString();

        const compItem = {
          id: parseInt(variantId),
          quantity: quantity.value,
          properties: {
            _msrp: originalPrice,
            _product_bundle_title: title,
            _product_bundle_instance: timestamp,
            _product_bundle_items: components.length.toString(),
            _bundle_url: bundleUrl,
            _bundle_productId: productId,
            _bundle_variantId: variantId,
            _bundle_savings: savings,
            _category_discount: categoryDiscount,
            _category_id: categoryPromoName,
            _bundle_discount: bundleDiscount,
            _tpr_discount: String(tprDiscount),
            Ships: variant?.shipsBy?.value,
          },
        };
        current++;
        bundle.push(compItem);
        return bundle;
      }, [])
      .reverse();
    payload = { items: payload };
  } else {
    const variant = isQV.value ? props.data.selected.variant : store.$state.selected.variant;
    const id = variant.id.split('ProductVariant/')[1].toString().trim();
    const comparePrice = variant?.compareAtPrice?.amount;
    const price = variant.price.amount;
    const tags = item.tags;
    const originalPrice = comparePrice > 1 ? comparePrice : price;
    const tprReduction = comparePrice > 1;

    const isLastCall = () => tags.includes('Last Call Assortment');
    const isCategory = () => {
      let categoryTag = tags.some((tag) => tag.includes('Category Promo:'));
      // Adding the not last call check in the case that a category tag accidentally gets added to a last call prod. (Site ops accidentally does this sometimes, so this is a safeguard)
      return categoryTag && !isLastCall();
    };
    // Temporary Price Reductions are variants that get a reduction but can be part of an active sale

    let discount = 'null';
    let categoryDiscount = 'null';
    const tprDiscount = findTprDiscount(item, variant);

    const categoryPromoName = isCategory() ? categoryId : 'null';
    if (isCategory()) {
      categoryDiscount = tags
        .find((tag) => tag.includes('Category Promo:'))
        ?.split(':')[1]
        .trim();
    }
    if (isLastCall()) {
      discount = tags
        .find((tag) => tag.includes('Discount Amount:'))
        ?.split(':')[1]
        .trim();
    }

    payload = {
      id: parseInt(id),
      quantity: quantity.value,
      properties: {
        _msrp: originalPrice,
        _category_id: categoryPromoName,
        _category_discount: categoryDiscount,
        _isGiftCard: props.isGiftCard.toString(),
        Ships: variant?.shipsBy?.value,
        _lc_discount: discount,
        _tpr_discount: String(tprDiscount),
      },
    };
  }

  const event = createEvent(payload);
  document.dispatchEvent(event);

  if (isQV.value) {
    store.$state.isQV = false;
    return emits('close');
  }
};

const handleEdit = async () => {
  const components = store.$state.components;
  const handle = props?.data?.selected.handle;
  const index = components.findIndex((component) => component.includes(handle));

  const options = props.data.selected.variant.selectedOptions;
  const variables = { handle, options };

  const request = { variables, query: COMPONENT_FROM_SELECTIONS_QUERY };
  const response = await fetchStorefront(request).then((res) => {
    const item = clean(res?.data?.product) ?? false;
    if (item?.promotedOptionGroups) {
      const groups = store.formatPOGs(item);
      item.promotedOptionGroups = groups;
    }
    return item;
  });
  store.$state.selected.components[index] = response;
  store.handleComponentAvailability();
  return emits('close');
};

const available = computed(() => {
  const isEditing = props?.edit ?? false;

  if (props.isGiftCard) return true;
  if (isEditing) {
    const item = props?.data ?? false;
    if (!item) return false;

    const variant = item?.selected?.variant;
    const isAvailable = variant.quantityAvailable > 0;
    return isAvailable;
  }

  const isStepped = store.isSteppedBundle;
  if (isStepped) {
    const current = store.getCurrentStep;
    const atReview = store.isReviewStep;
    if (atReview) return true;
    return current.selected.available;
  } else {
    return store.$state.selected.available;
  }
});

const maxAllowedQuantity = computed(() => {
  const isSteppedBundle = store.isSteppedBundle && !isQV.value;
  const themeSetting = parseInt(store.$state.settings.max_inventory);
  if (isSteppedBundle) return store.createRange(themeSetting);

  const data = props?.data ? props.data : store.$state;
  const variant = data.selected.variant ?? false;
  if (!variant) return store.createRange(themeSetting);

  const variantInventory = variant?.quantityAvailable ?? false;
  const hasLessInventory = variantInventory < themeSetting;

  if (hasLessInventory && variantInventory > 0) {
    return store.createRange(variantInventory);
  } else {
    return store.createRange(themeSetting);
  }
});

const handleCtaMessaging = () => {
  const isBuildStep = store.isBuildStep;
  const isEditing = props?.edit ?? false;
  switch (true) {
    case isEditing:
      const variant = props?.data?.selected?.variant ?? false;
      const hasInventory = variant.quantityAvailable > 0;
      cta.value = store.$state.terms.cta.edit;
      if (hasInventory) {
        if (isQV.value) cta.value = store.$state.terms.cta.default;
        else cta.value = store.$state.terms.cta.edit;
      } else {
        cta.value = store.$state.terms.cta.unavailable;
      }
      break;
    case isBuildStep:
      cta.value = store.$state.terms.cta.build;
      break;
    default:
      if (!available.value) {
        cta.value = store.$state.terms.cta.unavailable;
      } else {
        cta.value = store.$state.terms.cta.default;
      }
      break;
  }
};

const readyToProceed = computed(() => store.stepStatus);
const nextCta = computed(() => {
  const current = store.getCurrentStep;
  const { totalOptions } = current;
  const { options, available } = current.selected;
  const selections = options.filter((option) => option !== '');
  const enoughSelectionsMade = selections.length >= totalOptions;
  if (enoughSelectionsMade && !available) {
    return store.$state.terms.cta.unavailable;
  } else {
    return store.$state.terms.cta.next;
  }
});

watchEffect(() => handleCtaMessaging());
watchEffect(() => {
  if (ctaEl.value) {
    const event = new CustomEvent('cta:updated');
    document.dispatchEvent(event);
  }
});

onMounted(() => {
  fetchCartInstances();
});
</script>
